import { useEffect, useState } from 'react';
import { appTimezone } from '../config';
import { currentAppMonth } from '../utils/dateUtils';
import dayjs from '@utils/dayjs';

/** The largest delay setTimeout can hold (~24.8 days). Anything above it overflows the 32-bit signed
 *  int and fires IMMEDIATELY, so a wait of up to 31 days has to be split. */
const MAX_TIMEOUT = 2 ** 31 - 1;

/**
 * CALL-781: the current month in the app's configured timezone, refreshed when the clock crosses into
 * the next one.
 *
 * The Budget Overview can sit mounted for days. Reading the month once at mount froze the "hide past
 * months" cutoff there, so on the 1st the month that had just ended kept its column — through any number
 * of unrelated re-renders — until the user toggled the checkbox or paged the year. Returning it as state
 * gives the consuming memos a dep that actually changes at the boundary.
 */
export const useCurrentAppMonth = (): string => {
    const [month, setMonth] = useState<string>(currentAppMonth);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout>;

        const scheduleNextCheck = () => {
            const nextMonth = dayjs().tz(appTimezone).add(1, 'month').startOf('month');
            // A second past the boundary, so the wake-up is never a hair early and does not need a retry.
            const untilBoundary = Math.max(nextMonth.diff(dayjs()), 0) + 1000;
            timer = setTimeout(
                () => {
                    // Clamped waits wake early with the month unchanged; setMonth is then a no-op re-render
                    // (same string), and we simply re-arm for the remainder.
                    setMonth(currentAppMonth());
                    scheduleNextCheck();
                },
                Math.min(untilBoundary, MAX_TIMEOUT)
            );
        };

        scheduleNextCheck();
        return () => clearTimeout(timer);
    }, []);

    return month;
};
